import { useCallback, useEffect, useId, useState } from 'react';
import { CheckCircle2, Settings } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { IntegrationAppProvider, useIntegrationApp } from '@membranehq/react';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { CompanyLogo, getDomainFromCompany } from '../../../../components/ui/CompanyLogo';
import { connectorsApi } from '../../../../services/connectorsApi';
import { generateIntegrationAppToken } from '../../../../services/integrationAppEndpoint';
import { ConnectorApprovalPreferences, ConnectorTool } from './ConnectorApprovalPreferences';
import notify from '../../../../utils/notify';

// Providers that have a dedicated workspace-level settings page
// (sync configuration, field mappings, etc). For these, "Manage connection"
// navigates to that page so users get ALL the existing controls.
// For agent-only connectors (Linear, Notion), Manage just opens a
// Disconnect confirmation via the Membrane SDK.
const WORKSPACE_SETTINGS_PATH: Record<string, string> = {
  gmail: 'gmail',
  google_calendar: 'google-calendar',
};

interface AvailableKit {
  provider: string;
  display_name: string;
  description?: string;
  icon?: string;
  capabilities?: string[];
  tools: ConnectorTool[];
}

interface ConnectedAccount {
  id: string;
  provider: string;
  primary_email: string;
  is_active: boolean;
  is_default_for_member: boolean;
  reconnect_needed: boolean;
  // connection_id retained for backward-compat with any existing callers
  connection_id?: string;
}

/**
 * The root <IntegrationAppProvider> in HomePage.tsx is configured with a
 * workspace-level token. Personal (member-level) connectors need the OAuth
 * popup to identify the user as `{workspace_id}:{user_id}` — otherwise
 * every user in the workspace would share one Membrane identity and their
 * personal connections would collide. We nest a provider here that fetches
 * a member-level token; the SDK calls inside this subtree will now scope
 * connections per-user, matching what the backend's member-level token
 * expects when it later queries those connections.
 */
export function PersonalConnectorsTab({ workspaceId }: { workspaceId: string }) {
  const fetchMemberToken = useCallback(async () => {
    if (!workspaceId) return null;
    try {
      const res = await generateIntegrationAppToken({
        workspace_id: workspaceId,
        isMemberLevel: true,
      });
      return res?.data?.token ?? null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch member-level Membrane token', err);
      return null;
    }
  }, [workspaceId]);

  return (
    <IntegrationAppProvider fetchToken={fetchMemberToken}>
      <PersonalConnectorsTabInner workspaceId={workspaceId} />
    </IntegrationAppProvider>
  );
}

function PersonalConnectorsTabInner({ workspaceId }: { workspaceId: string }) {
  const { workspaceId: workspaceSlug } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const integrationApp = useIntegrationApp();

  const [available, setAvailable] = useState<AvailableKit[]>([]);
  const [connected, setConnected] = useState<ConnectedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  // Track in-flight default-change requests per provider to prevent races
  const [settingDefault, setSettingDefault] = useState<Record<string, boolean>>({});
  // Track in-flight connect/disconnect flows per provider
  const [connecting, setConnecting] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!workspaceId) return;

    setLoading(true);
    // Reconcile any rows missing options.connection_id against Membrane BEFORE
    // we fetch /connectors/me, so the UI always reflects a post-backfill state.
    // If backfill fails (Membrane down, etc.) we still proceed — /me returns
    // whatever's already in the DB.
    connectorsApi.backfillConnectionIds(workspaceId)
      .catch(() => { /* non-fatal — proceed with existing state */ })
      .finally(() => {
        Promise.all([
          connectorsApi.getAvailable(workspaceId).catch(() => ({ data: { connectors: [] } })),
          connectorsApi.getMine(workspaceId).catch(() => ({ data: { connected: [] } })),
        ]).then(([availRes, mineRes]: any[]) => {
          // Backend returns {connectors: [...]} — unwrap to the array.
          // (Tolerate the old bare-list shape in case a stale backend is deployed.)
          const rawAvail = availRes?.data;
          const connectors = Array.isArray(rawAvail)
            ? rawAvail
            : Array.isArray(rawAvail?.connectors)
              ? rawAvail.connectors
              : [];
          setAvailable(connectors);
          setConnected(mineRes?.data?.connected ?? []);
          setLoading(false);
        });
      });
  }, [workspaceId]);

  const handleSetDefault = async (provider: string, primaryEmail: string) => {
    if (settingDefault[provider]) return;

    // Optimistic update
    const prev = connected;
    setConnected((cs) =>
      cs.map((c) =>
        c.provider === provider
          ? { ...c, is_default_for_member: c.primary_email === primaryEmail }
          : c,
      ),
    );
    setSettingDefault((s) => ({ ...s, [provider]: true }));

    try {
      await connectorsApi.setDefaultAccount(workspaceId, provider, primaryEmail);
    } catch {
      // Revert on failure
      setConnected(prev);
      notify({
        type: 'failure',
        heading: 'Could not update default',
        messageList: ['Failed to set default account. Please try again.'],
      });
    } finally {
      setSettingDefault((s) => ({ ...s, [provider]: false }));
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary/30 border-t-primary inline-block" />
      </div>
    );
  }

  if (!available.length) {
    return (
      <div className="py-12 text-center">
        <p className="text-[13px] text-muted-foreground">No personal connectors available yet.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {available.map((kit) => {
        const myAccounts = connected.filter((c) => c.provider === kit.provider);
        const hasConnection = myAccounts.length > 0;
        const needsReconnect = myAccounts.some((c) => c.reconnect_needed);
        const multiAccount = myAccounts.length >= 2;
        const isConnecting = !!connecting[kit.provider];

        const handleConnect = async () => {
          if (isConnecting || !integrationApp) return;
          setConnecting((s) => ({ ...s, [kit.provider]: true }));
          try {
            // Membrane SDK — opens the OAuth popup for this provider.
            // On success returns the new Connection object with id + state.
            const connection: any = await integrationApp
              .integration(kit.provider)
              .openNewConnection();

            if (!connection || connection.state !== 'READY' || !connection.id) {
              // User cancelled or OAuth returned an unusable state
              return;
            }

            // Optimistically tell our backend so options.connection_id lands
            // immediately — the Membrane webhook will reconcile within seconds.
            try {
              const primaryEmail =
                connection.name ||
                connection.accountData?.email ||
                connection.accountData?.emailAddress ||
                connection.accountData?.primaryEmail ||
                '';
              await connectorsApi.connectionCreated(workspaceId, kit.provider, {
                connection_id: connection.id,
                primary_email: primaryEmail || `${kit.provider}@${connection.id}`,
                external_account_id: connection.accountId,
              });
            } catch {
              // Non-fatal — the webhook path is canonical and will populate
              // within a moment. Fall back to backfill on reload.
            }

            // Refresh the card list so the new connection shows up.
            const mineRes = await connectorsApi.getMine(workspaceId)
              .catch(() => ({ data: { connected: [] } }));
            setConnected(mineRes?.data?.connected ?? []);

            notify({
              type: 'success',
              heading: `${kit.display_name} connected`,
              messageList: ['You can now use ORA to act on this integration.'],
            });
          } catch (err: any) {
            // User cancelled the OAuth popup usually throws — swallow silently.
            if (err?.message && !/cancel|closed|abort/i.test(err.message)) {
              notify({
                type: 'failure',
                heading: `Couldn't connect ${kit.display_name}`,
                messageList: [err.message ?? 'Please try again.'],
              });
            }
          } finally {
            setConnecting((s) => ({ ...s, [kit.provider]: false }));
          }
        };

        const hasWorkspacePage = !!WORKSPACE_SETTINGS_PATH[kit.provider];

        const handleManage = async () => {
          // Gmail / Google Calendar already have a full workspace-level
          // settings page with sync config — navigate there. Everything
          // ORA needs is already wired up: account details, reconnect,
          // disconnect, sync toggles, etc.
          if (hasWorkspacePage) {
            navigate(
              `/home/${workspaceSlug}/settings/integrations/${WORKSPACE_SETTINGS_PATH[kit.provider]}`,
            );
            return;
          }

          // Agent-only connectors (Linear, Notion) — confirm + disconnect.
          if (isConnecting || !integrationApp || myAccounts.length === 0) return;
          const ok = typeof window !== 'undefined'
            ? window.confirm(
                `Disconnect ${kit.display_name}? ORA will lose the ability to ` +
                `act on this account until you reconnect.`,
              )
            : true;
          if (!ok) return;

          setConnecting((s) => ({ ...s, [kit.provider]: true }));
          try {
            // Archive each active connection for this provider on Membrane's side.
            // The webhook reconciles our DB within seconds; we also refresh locally.
            for (const acct of myAccounts) {
              const cid = acct.connection_id;
              if (!cid) continue;
              try {
                await integrationApp.connection(cid).archive();
              } catch {
                /* one connection failing shouldn't abort the batch */
              }
            }
            const mineRes = await connectorsApi.getMine(workspaceId)
              .catch(() => ({ data: { connected: [] } }));
            setConnected(mineRes?.data?.connected ?? []);
            notify({
              type: 'success',
              heading: `${kit.display_name} disconnected`,
              messageList: ['You can reconnect anytime from this page.'],
            });
          } catch (err: any) {
            notify({
              type: 'failure',
              heading: `Couldn't disconnect ${kit.display_name}`,
              messageList: [err?.message ?? 'Please try again.'],
            });
          } finally {
            setConnecting((s) => ({ ...s, [kit.provider]: false }));
          }
        };

        return (
          <div
            key={kit.provider}
            // `self-start` + explicit grid-row span of 1 so cards don't
            // stretch to match the tallest sibling. Each card has its own
            // natural height.
            className="flex h-full flex-col gap-4 self-start rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
          >
            {/* Header — logo + name + status */}
            <div className="flex items-start gap-3">
              <CompanyLogo
                domain={getDomainFromCompany(kit.display_name)}
                name={kit.display_name}
                className="h-10 w-10 flex-shrink-0 rounded-md"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-semibold text-foreground">
                    {kit.display_name}
                  </span>
                  {hasConnection && !needsReconnect && (
                    <span
                      className="inline-flex h-2 w-2 rounded-full bg-status-active"
                      aria-label="Connected"
                      title="Connected"
                    />
                  )}
                  {needsReconnect && (
                    <Badge
                      variant="statusPill"
                      status="warning"
                      className="text-[11px]"
                    >
                      Reconnect needed
                    </Badge>
                  )}
                </div>
                {kit.description && (
                  <p className="mt-1 text-[12px] leading-[18px] text-muted-foreground">
                    {kit.description}
                  </p>
                )}
              </div>
            </div>

            {/* Capabilities — full-text, bulleted */}
            {kit.capabilities && kit.capabilities.length > 0 && (
              <ul className="space-y-1.5 text-[12px] leading-[18px] text-foreground/80">
                {kit.capabilities.map((cap) => (
                  <li key={cap} className="flex items-start gap-2">
                    <span
                      aria-hidden="true"
                      className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-muted-foreground/70"
                    />
                    <span>{cap}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Connected account(s) */}
            {hasConnection && (
              <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2">
                {multiAccount ? (
                  <AccountRadioGroup
                    accounts={myAccounts}
                    provider={kit.provider}
                    disabled={!!settingDefault[kit.provider]}
                    onSetDefault={handleSetDefault}
                  />
                ) : (
                  myAccounts.map((acct) => (
                    <div
                      key={acct.id ?? acct.primary_email}
                      className="flex items-center gap-1.5 text-[12px] text-foreground"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-status-active" />
                      <span className="truncate">{acct.primary_email}</span>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Spacer pushes CTA to the bottom of the card */}
            <div className="flex-1" />

            {/* CTA — Settings convention uses `outline` throughout;
                avoid the purple `default` variant. */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-full text-[12px]"
              onClick={hasConnection ? handleManage : handleConnect}
              disabled={isConnecting}
            >
              {isConnecting
                ? (hasConnection ? 'Disconnecting…' : 'Connecting…')
                : hasConnection
                  ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Settings className="h-3.5 w-3.5" />
                      {hasWorkspacePage ? 'Manage settings' : 'Disconnect'}
                    </span>
                  )
                  : 'Connect'}
            </Button>

            {/* Approval preferences (only when connected) */}
            {hasConnection && (
              <ConnectorApprovalPreferences
                workspaceId={workspaceId}
                provider={kit.provider}
                tools={kit.tools ?? []}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AccountRadioGroup — rendered only when a provider has 2+ accounts
// ---------------------------------------------------------------------------

interface AccountRadioGroupProps {
  accounts: ConnectedAccount[];
  provider: string;
  disabled: boolean;
  onSetDefault: (provider: string, primaryEmail: string) => void;
}

function AccountRadioGroup({ accounts, provider, disabled, onSetDefault }: AccountRadioGroupProps) {
  // Stable group name so label[for] wiring works across re-renders
  const groupName = useId();

  return (
    <fieldset
      className="space-y-1.5"
      aria-label={`Default account for ${provider}`}
      disabled={disabled}
    >
      {accounts.map((acct) => {
        const inputId = `${groupName}-${acct.primary_email}`;
        const isDefault = acct.is_default_for_member;

        return (
          <label
            key={acct.id ?? acct.primary_email}
            htmlFor={inputId}
            className={[
              'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-[12px] transition-colors',
              disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent/50',
            ].join(' ')}
          >
            {/* Native radio — visually styled with Tailwind accent */}
            <input
              id={inputId}
              type="radio"
              name={groupName}
              value={acct.primary_email}
              checked={isDefault}
              disabled={disabled}
              onChange={() => {
                if (!isDefault) onSetDefault(provider, acct.primary_email);
              }}
              className="h-3.5 w-3.5 cursor-pointer accent-foreground disabled:cursor-not-allowed"
            />

            {/* Status dot */}
            {acct.reconnect_needed ? (
              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-priority-medium" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-status-active" />
            )}

            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {acct.primary_email}
            </span>

            {/* "Default" pill — only on the selected account */}
            {isDefault && (
              <span className="flex-shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                Default
              </span>
            )}
          </label>
        );
      })}

      {/* Micro-hint so users understand the radio's purpose */}
      <p className="pl-2 pt-0.5 text-[11px] text-muted-foreground/70">
        Agent uses the default account when no email is specified.
      </p>
    </fieldset>
  );
}
